import React, { useImperativeHandle } from "react";
// eslint-disable-next-line no-unused-vars
import * as FxLog from '../fx/FxLog';
import { makeStyles } from '@material-ui/core/styles';
import CxIconButton from './CxIconButton';
import { useTranslation } from '../hx/useTranslation';
import { useImageIO } from '../hx/useImageIO';
import { Stage, Layer, Line, Image as ImageKonva } from 'react-konva';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useWindowSize } from '../hx/useWindowSize';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    //width: '100%',
    gap: 3,
    border: '1px solid var(--ibs_color_gray)',
    borderRadius: '2px',
  },
  containerFullscreen: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 1,
    padding: '0px 0 0px 0',
    background: 'var(--ibs_color_gray_light)',
  },
});

const CxImageEditor = props => {
  const [{ trans }] = useTranslation();
  const [{ imageLoad, imageSave, imageDelete, imageResize, imageCreateBlank }] = useImageIO();
  const classes = useStyles();
  const size = useWindowSize();
  const { onSelect, onClear } = props;

  const [img, setImg] = React.useState(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const rf_inputfile = React.useRef(null);
  const rf_container = React.useRef(null);
  const rf_scroll = React.useRef(null);
  const rf_stage = React.useRef(null);
  const rf_layer = React.useRef(null);
  const rf_image_konva = React.useRef(null);

  const [color, setColor] = React.useState('');
  const [lines, setLines] = React.useState([]);
  const isDrawing = React.useRef(false);

  React.useEffect(() => {
    if (props.blank) {
      loadCanvas(imageCreateBlank(props.blank.width, props.blank.height));
    }
    if (props.pen_black_default) {
      setColor('black');
    }
    if (props.pen_red_default) {
      setColor('red');
    }

    Promise.all([imageLoad(props.url, props.inXXX)]).then((result) => {
      loadCanvas(result[0]);
    });

    // eslint-disable-next-line
  }, []);

  useImperativeHandle(props.xref, () => {
    return {
      getType, doSave, doDelete, getBase64
    };
  });
  
  const getType = () => { return 'CxImageEditor' };
  const getBase64 = () => { return rf_image_konva.current ? rf_image_konva.current.getStage().toDataURL() : null };

  const loadCanvas = (base64) => {
    let htmlImg = new Image();
    htmlImg.src = base64;
    htmlImg.onload = function (e) {
      //cvs.height = img.height * cvs.width / img.width;
      setImg(htmlImg);
      if (onSelect) {
        onSelect();
      }
    }
  }

  const doSave = (inXXX) => {
      if (rf_image_konva.current) {
      return Promise.all([imageSave(props.url, inXXX, rf_image_konva.current.getStage().toDataURL())]).then((result) => {
        FxLog.infox('CxImageEditor.doSave', '...image saved!!');
        return result[0];
      });
    } else {
      return Promise.all([imageSave(props.url, inXXX, null)]).then((result) => {
        FxLog.infox('CxImageEditor.doSave', '...image saved!!');
        return result[0];
      });
    }
  };
  
  const doDelete = (inXXX) => {
    imageDelete(props.url, inXXX);
  };
  
  const clear = () => {
    rf_inputfile.current.value = '';
    clearLayer();
    Promise.all([setImg(null)]).then((result) => {
      if (props.blank) {
        loadCanvas(imageCreateBlank(props.blank.width, props.blank.height));
      }
      if (onClear) {
        onClear();
      }
    });
  };
  
  const clearLayer = () => {
    setLines([]);
    if (rf_layer.current) {
      rf_layer.current.draw();
    }
  };

  const onChangeFile = (event) => {
    event.stopPropagation();
    event.preventDefault();
    var file = event.target.files[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.png') || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg')) {
        // reducimos tamaño del archivo para no ocupar demasiado en db.
        Promise.all([imageResize(file)]).then((result) => {
          loadCanvas(result[0]);
        });
      } else {
        clear();
      }
    }
  }

  const handleMouseDown = (e) => {
    if (color && size.isLarge) {
      isDrawing.current = true;
      const pos = e.target.getStage().getPointerPosition();
      setLines([...lines, { color: color, points: [pos.x, pos.y] }]);
    }
  };

  const handleMouseMove = (e) => {
    // no drawing - skipping
    if (!isDrawing.current) {
      return;
    }
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    let lastLine = lines[lines.length - 1];
    // add point
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    // replace last
    lines.splice(lines.length - 1, 1, lastLine);
    setLines(lines.concat());
  };

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getContainerDimensions = () => {
    if (!img) return { width: 0, height: 0 };
    
    if (isFullscreen) {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight - 60; // Restar altura de la barra de botones
      
      // Calcular el tamaño manteniendo la proporción
      const imgRatio = img.width / img.height;
      const containerRatio = windowWidth / windowHeight;
      
      if (imgRatio > containerRatio) {
        // La imagen es más ancha proporcionalmente
        return {
          width: windowWidth,
          height: windowWidth / imgRatio
        };
      } else {
        // La imagen es más alta proporcionalmente
        return {
          width: windowHeight * imgRatio,
          height: windowHeight
        };
      }
    } else {
      return {
        width: size.isLarge ? img.width : 'var(--ibs_control_width_lg)',
        height: size.isLarge ? img.height : 'var(--ibs_control_width_lg)'
      };
    }
  };

  return (
    <div ref={rf_container} className={isFullscreen ? classes.containerFullscreen : classes.container} >
      <div className={classes.buttonContainer}>
        <input type="file" accept="image/jpeg, image/jpg, image/png" ref={rf_inputfile} style={{ display: "none" }} onChange={onChangeFile} />
        {props.can_search && <CxIconButton type="search_image" classType="ibs_iconbutton_toolbar" onClick={() => rf_inputfile.current.click()} tooltip={trans('field.file_upload')} />}
        <CxIconButton type="delete2" classType="ibs_iconbutton_toolbar" onClick={() => clear()} tooltip={trans('field.delete')} />
        {img && (
          <CxIconButton 
            type={isFullscreen ? "zoom_out" : "zoom_in"} 
            classType="ibs_iconbutton_toolbar" 
            onClick={toggleFullscreen} 
            //tooltip={isFullscreen ? trans('field.expand_contract') : trans('field.expand_contract')} 
          />
        )}
        {props.pen_black && size.isLarge && <CxIconButton type="pencil" classType="ibs_iconbutton_toolbar" onClick={() => setColor('black')} style={{ color: 'black' }} />}
        {props.pen_red && size.isLarge && <CxIconButton type="pencil" classType="ibs_iconbutton_toolbar" onClick={() => setColor('red')} style={{ color: 'red' }} />}
        {props.pen_blue && size.isLarge && <CxIconButton type="pencil" classType="ibs_iconbutton_toolbar" onClick={() => setColor('blue')} style={{ color: 'blue' }} />}
        {props.pen_yellow && size.isLarge && <CxIconButton type="pencil" classType="ibs_iconbutton_toolbar" onClick={() => setColor('yellow')} style={{ color: 'yellow' }} />}
      </div>
      
      {img && <ScrollContainer
        ref={rf_scroll}
        hideScrollbars={true}
        style={getContainerDimensions()}>
        <Stage ref={rf_stage}
          width={isFullscreen ? getContainerDimensions().width : img.width}
          height={isFullscreen ? getContainerDimensions().height : img.height}
          onMouseDown={handleMouseDown}
          onMousemove={handleMouseMove}
          onMouseup={handleMouseUp}
          listening={size.isLarge} // Desactivar eventos en móvil para permitir scroll
          //style={{ background: 'yellow' }}
        >
          <Layer ref={rf_layer}>
            <ImageKonva 
              ref={rf_image_konva} 
              image={img} 
              width={isFullscreen ? getContainerDimensions().width : img.width}
              height={isFullscreen ? getContainerDimensions().height : img.height}
            />
            {lines.map((line, i) => {
              const scaleX = isFullscreen ? getContainerDimensions().width / img.width : 1;
              const scaleY = isFullscreen ? getContainerDimensions().height / img.height : 1;
              
              return (
                <Line 
                  key={i}
                  points={line.points.map((point, index) => 
                    index % 2 === 0 ? point * scaleX : point * scaleY
                  )}
                  stroke={line.color}
                  strokeWidth={(props.stroke||5) * (isFullscreen ? Math.min(scaleX, scaleY) : 1)}
                  tension={0.5}
                  lineCap="round"
                  lineJoin="round"
                  globalCompositeOperation='source-over'
                />
              );
            })}
          </Layer>
        </Stage>
      </ScrollContainer>
      }
    </div >
  );

};

export default CxImageEditor;